// src/features/developer/jwt-tool/JwtTool.tsx
import React, { useState } from 'react';
import {
    Box, Grid, Paper, Tabs, Tab, TextField, Typography, Button, Stack, Chip, Alert, Tooltip, IconButton, Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useJwtTool } from '../hooks/useJwtTool';
import JwtViewer from '../components/JwtViewer';
import JwtPartEditor from '../components/JwtPartEditor';
import StyledFAQ from '../../common/components/StyledFAQ';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const JwtTool: React.FC = () => {
    const { t } = useTranslation();
    const {
        mode,
        switchMode,
        encodedJwt,
        header,
        payload,
        secret,
        headerObj,
        payloadObj,
        isJwtFormatValid,
        isSignatureValid,
        error,
        handleEncodedJwtChange,
        handleHeaderChange,
        handlePayloadChange,
        handleSecretChange,
        generateExample,
        clearAll,
    } = useJwtTool();

    const [secretCopied, setSecretCopied] = useState(false);

    // Handle copying secret to clipboard
    const handleCopySecret = () => {
        navigator.clipboard.writeText(secret);
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2000);
    };

    // Render signature status chip
    const SignatureStatus = () => {
        if (isSignatureValid === true) {
            return <Chip icon={<CheckCircleIcon />} label={t('jwt_tool_signature_valid', { ns: 'tools'})} color="success" size="small" />;
        }
        if (isSignatureValid === false) {
            return <Chip icon={<CancelIcon />} label={t('jwt_tool_signature_invalid', { ns: 'tools'})} color="error" size="small" />;
        }
        return <Chip icon={<HelpOutlineIcon />} label={t('jwt_tool_signature_not_verified', { ns: 'tools'})} color="warning" size="small" />;
    };

    const faqs = [
        { q: 'jwt_tool_faq1_q', a: 'jwt_tool_faq1_a' },
        { q: 'jwt_tool_faq2_q', a: 'jwt_tool_faq2_a' },
        { q: 'jwt_tool_faq3_q', a: 'jwt_tool_faq3_a' },
    ];

    // Left panel content (encoded/decoded based on mode)
    const leftPanel = (
        <Stack spacing={2} sx={{ height: '100%', width: '100%' }}>
            <Typography variant="h6">{t(mode === 'decoder' ? 'jwt_tool_encoded' : 'jwt_tool_decoded', { ns: 'tools'})}</Typography>
            {mode === 'decoder' ? (
                <JwtViewer
                    value={encodedJwt}
                    onChange={handleEncodedJwtChange}
                    placeholder={t('jwt_tool_token_placeholder', { ns: 'tools'})}
                    readOnly={false}
                    sx={{ flexGrow: 1 }}
                    {...{} as any}
                />
            ) : (
                <>
                    <JwtPartEditor
                        title={t('jwt_tool_header', { ns: 'tools'})}
                        data={headerObj}
                        rawData={header}
                        onRawDataChange={handleHeaderChange}
                        readOnly={false}
                        sx={{ flexGrow: 1 }}
                        {...{} as any}
                    />
                    <JwtPartEditor
                        title={t('jwt_tool_payload', { ns: 'tools'})}
                        data={payloadObj}
                        rawData={payload}
                        onRawDataChange={handlePayloadChange}
                        readOnly={false}
                        sx={{ flexGrow: 1 }}
                        {...{} as any}
                    />
                </>
            )}
        </Stack>
    );

    // Right panel content (encoded/decoded based on mode)
    const rightPanel = (
        <Stack spacing={2} sx={{ height: '100%', width: '100%' }}>
            <Typography variant="h6">{t(mode === 'encoder' ? 'jwt_tool_encoded' : 'jwt_tool_decoded', { ns: 'tools'})}</Typography>
            {mode === 'encoder' ? (
                <JwtViewer
                    value={encodedJwt}
                    readOnly={true}
                    sx={{ flexGrow: 1 }}
                    {...{} as any}
                />
            ) : (
                <>
                    <JwtPartEditor
                        title={t('jwt_tool_header', { ns: 'tools'})}
                        data={headerObj}
                        rawData={header}
                        onRawDataChange={handleHeaderChange}
                        readOnly={true}
                        sx={{ flexGrow: 1 }}
                        {...{} as any}
                    />
                    <JwtPartEditor
                        title={t('jwt_tool_payload', { ns: 'tools'})}
                        data={payloadObj}
                        rawData={payload}
                        onRawDataChange={handlePayloadChange}
                        readOnly={true}
                        sx={{ flexGrow: 1 }}
                        {...{} as any}
                    />
                </>
            )}
        </Stack>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Tabs value={mode} onChange={(e, newMode) => switchMode(newMode)} centered>
                    <Tab label={t('jwt_tool_decoder', { ns: 'tools'})} value="decoder" />
                    <Tab label={t('jwt_tool_encoder', { ns: 'tools'})} value="encoder" />
                </Tabs>
            </Paper>

            {/* Main content with side-by-side panels and separator */}
            <Box sx={{ display: 'flex', minHeight: '600px', width: '100%' }}>
                <Box sx={{ flex: 1, pr: 1.5 }}>
                    {leftPanel}
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ flex: 1, pl: 1.5 }}>
                    {rightPanel}
                </Box>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8} {...{} as any}>
                        <TextField
                            label={t('jwt_tool_secret_key', { ns: 'tools'})}
                            value={secret}
                            onChange={handleSecretChange}
                            placeholder={t('jwt_tool_secret_placeholder', { ns: 'tools'})}
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputProps={{
                                endAdornment: (
                                    <Tooltip title={secretCopied ? t('jwt_tool_copied', { ns: 'tools'}) : t('jwt_tool_copy', { ns: 'tools'})}>
                                        <IconButton size="small" onClick={handleCopySecret} disabled={!secret}>
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} {...{} as any}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {mode === 'decoder' && isJwtFormatValid && <SignatureStatus />}
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            <Stack direction="row" spacing={2} sx={{ my: 3 }}>
                <Button variant="contained" onClick={generateExample}>{t('jwt_tool_generate_example', { ns: 'tools'})}</Button>
                <Button variant="outlined" color="secondary" onClick={clearAll}>{t('jwt_tool_clear', { ns: 'tools'})}</Button>
            </Stack>

            {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

            <Box sx={{ my: 15 }}>
                <Typography sx={{ mb: 2 }} variant="h4" component="h2" gutterBottom textAlign="center">
                    {t('jwt_tool_info_title', { ns: 'tools'})}
                </Typography>
                <Typography sx={{ mb: 10 }} paragraph color="text.secondary" textAlign="center" maxWidth="800px" mx="auto">
                    {t('jwt_tool_info_desc', { ns: 'tools'})}
                </Typography>
                <StyledFAQ
                    title={t('jwt_tool_faq_title', { ns: 'tools'})}
                    faqs={faqs}
                    t={t}
                />
            </Box>
        </Box>
    );
};

export default JwtTool;